import { Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, Tooltip } from '@mui/material'
import { push } from '@lagunovsky/redux-react-router'
import { chunk, flatten, groupBy, orderBy, pick, template } from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import AdministrationAction from '../../../administration/actions/AdministrationAction'
import { setActions } from '../../../components/ActionUtil'
import ProgressCard from '../../../components/card/ProgressCard'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import NumberField from '../../../components/forms/NumberField'
import Select from '../../../components/forms/Select'
import FilterSelect from '../../../components/forms/specific/FilterSelect'
import JobAction from '../../../import/actions/JobAction'
import NetworkAction from '../../../referencial/components/network/actions/NetworkAction'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import { catchError } from '../../../utils/ActionUtils'
import { getDate, getFullDate } from '../../../utils/DateUtil'
import { onChangeDate } from '../../../utils/FormUtils'
import { round } from '../../../utils/NumberUtil'
import WaitAction from '../../../wait/WaitAction'
import PiezometryAction from '../../actions/PiezometryAction'
import DtoExportOverview from './DtoExportOverview'
import { CardTable, NewTable } from 'components/datatable/NewTable'
import { darkBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { promiseAllProgress } from '../../../utils/ActionUtils'
import { rotate } from '../../../utils/AnimationsUtils'
import { NoDataCard } from 'components/card/NoDataCard'

const CHUNK_SIZE = 200

const ExporOverviewList = ({}) => {
    const {
        networkPiezoLink,
        networks,
        filterResults,
    } = useSelector(store => ({
        networkPiezoLink: store.PiezometryReducer.networkPiezoLink,
        networks: store.NetworkReducer.networks,
        filterResults: store.StationReducer.filterResults,
    }), shallowEqual)

    const statusList = [
        { code: 1, name: i18n.success },
        { code: 2, name: i18n.successWithWarnings },
        { code: 3, name: i18n.error },
    ]
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)

    const [networkList, setNetworkList] = useState([])
    const [exportData, setExportData] = useState([])
    const [redCount, setRedCount] = useState(null)
    const [orangeCount, setorangeCount] = useState(null)
    const [greenCount, setGreenCount] = useState(null)
    const [logDialogIsOpen, setLogDialogIsOpen] = useState(false)
    const [selectedStationCode, setSelectedStationCode] = useState(null)
    const [selectedExecutionId, setSelectedExecutionId] = useState(null)
    const [selectedStatusLabel, setSelectedStatusLabel] = useState(null)
    const [jobLogs, setJobLogs] = useState([])
    const [date, setDate] = useState(moment().valueOf())
    const [searchValue, setSearchValue] = useState(null)
    const [filter, setFilter] = useState(() => pick(AdministrationAction.getCache('piezometry'), ['filter']).filter)
    const [stationsFilter, setStationsFilter] = useState(filter && filterResults ? filterResults : null)
    const [network, setNetwork] = useState(null)
    const [color, setColor] = useState('all')
    const [exportStatus, setExportStatus] = useState(null)
    const [ackStatus, setAckStatus] = useState(null)
    const [lastExecStatus, setLastExecStatus] = useState(null)
    const [nbDaysWarning, setNbDaysWarning] = useState(3)
    const [nbDaysError, setNbDaysError] = useState(5)
    const dispatch = useDispatch()

    const piezoRefs = useRef(null)

    const getReferences = () => {
        if (piezoRefs.current) {
            return Promise.resolve(piezoRefs.current)
        }
        return Promise.all([
            PiezometryAction.promisePiezometersLight(),
            PiezometryAction.promiseNetworkPiezoLink(),
        ]).then(([piezometers, networkLinks]) => {
            piezoRefs.current = {
                piezometers: piezometers.map(p => ({ id: p[0], code: p[1], name: p[2] })),
                networkLinks,
            }
            return piezoRefs.current
        })
    }

    const onValidate = () => {
        setDataLoaded(false)
        setProgress(0)

        const hasStationFilter = ![-1, null, undefined].includes(filter)
        const filterIds = hasStationFilter ? new Set((stationsFilter || []).map(s => s.id || s)) : null

        getReferences().then(({ piezometers, networkLinks }) => {
            const hasNetwork = network !== null && network !== undefined && network !== ''
            const networkIds = hasNetwork
                ? new Set(networkLinks.filter(l => l.idNetwork == network).map(l => l.idPiezometer))
                : null

            const ids = piezometers
                .map(p => p.id)
                .filter(id => (!filterIds || filterIds.has(id)) && (!networkIds || networkIds.has(id)))

            if (!ids.length) {
                return []
            }

            const baseCriterias = {
                searchValue,
                filter,
                network,
                color,
                exportStatus,
                ackStatus,
                lastExecStatus,
                nbDaysWarning,
                nbDaysError,
                date,
            }
            const promises = chunk(ids, CHUNK_SIZE).map(group =>
                PiezometryAction.promisePiezoExportOverview({ ...baseCriterias, stationsFilter: group }),
            )
            return promiseAllProgress(promises, setProgress)
        }).then(jsons => {
            setExportData(flatten(jsons.map(j => j.lines)).map(d => new DtoExportOverview(d)))
            setRedCount(jsons.reduce((acc, j) => acc + (j.red || 0), 0))
            setorangeCount(jsons.reduce((acc, j) => acc + (j.orange || 0), 0))
            setGreenCount(jsons.reduce((acc, j) => acc + (j.green || 0), 0))
            setDataLoaded(true)
        }).catch(err => {
            setDataLoaded(true)
            catchError(err, i18n.exportOverview)
        })
    }

    const getStatusLabel = (status) => {
        switch (status) {
            case 1:
                return i18n.success
            case 2:
                return i18n.successWithWarnings
            case 3:
                return i18n.error
            default:
                return null
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'success':
                return { className: 'green-200' }
            case 'warning':
                return { className: 'yellow-200' }
            case 'error':
                return { className: 'red-200' }
            default:
                return null
        }
    }

    const getStatusIcon = (status) => {
        const size = 25
        const [icon, iconColor, label] = (() => {
            switch (status) {
                case 1:
                    return ['done', 'green', i18n.success]
                case 2:
                    return ['done', 'green', i18n.successWithWarnings]
                case 3:
                    return ['error', 'red', i18n.error]
                default:
                    return [null, null, null]
            }
        })()
        if (icon) {
            if (status === 2) {
                return (
                    <Tooltip title={label}>
                        <div style={{ position: 'relative', width: `${size}px` }} >
                            <Icon
                                sx={{
                                    animation: icon === 'sync' ? `${rotate} 2s linear infinite` : '',
                                    color: iconColor,
                                    fontSize: `${size}px`,
                                    position: 'relative',
                                    width: `${size}px`,
                                    height: `${size}px`,
                                }}
                            >
                                {icon}
                            </Icon>
                            <Icon
                                sx={{
                                    color: 'orange',
                                    fontSize: `${round(size/2, 0)}px`,
                                    width: `${round(size/2, 0)}px`,
                                    height: `${round(size/2, 0)}px`,
                                    position: 'absolute',
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 10,
                                }}
                            >
                                warning
                            </Icon>
                        </div>
                    </Tooltip>
                )
            }
            return (
                <Tooltip title={label}>
                    <Icon
                        sx={{
                            animation: icon === 'sync' ? `${rotate} 2s linear infinite` : '',
                            color: iconColor,
                            fontSize: `${size}px`,
                            width: `${size}px`,
                            height: `${size}px`,
                        }}
                    >
                        {icon}
                    </Icon>
                </Tooltip>
            )
        }
        return undefined
    }

    const redirectStation = (id, validation) => dispatch(push(`/station/piezometry/${id}${validation ? '/validation' : ''}`))

    const openLog = (stationCode, executionId, statusLabel) => {
        dispatch(WaitAction.waitStart())
        JobAction.promiseFindLog(executionId, stationCode).then(json => {
            dispatch(WaitAction.waitStop())
            setLogDialogIsOpen(true)
            setSelectedExecutionId(executionId)
            setSelectedStationCode(stationCode)
            setJobLogs(json)
            setSelectedStatusLabel(statusLabel)
        }).catch(err => {
            dispatch(WaitAction.waitStop())
            catchError(err, i18n.exportOverview)
        })
    }

    const values = useMemo(() => {
        const rawData = exportData.map(d => ({
            ...d,
            nullValue: { leftIcon: { icon: 'border_color', color: 'dodgerblue', tooltip: i18n.goToValidation }, onClick: () => redirectStation(d.stationId, true) },
            code: { value: d.code, onClick: () => redirectStation(d.stationId) },
            name: { value: d.name, onClick: () => redirectStation(d.stationId) },
            city: { value: d.city },
            startDate: { value: d.startDate, format: 'dd/MM/yyyy', cellType: 'date' },
            endDate: { value: d.endDate, format: 'dd/MM/yyyy', cellType: 'date' },
            numNum: { value: d.num, cellType: 'number' },

            nullValue2: { value: getStatusIcon(d.exportStatus), onClick: () => openLog(d.code, d.exportExecId, i18n.exportStatus) },
            exportName: { value: d.exportDate, color: d.exportColor, format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date', onClick: () => openLog(d.code, d.exportExecId, i18n.exportStatus) },
            exportStatus: { value: getStatusLabel(d.exportStatus) },

            nullValue3: { value: getStatusIcon(d.ackStatus), onClick: () => openLog(d.code, d.ackExecId, i18n.ackStatus) },
            acknoledgment: { value: d.ackDate, color: d.ackColor, format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date', onClick: () => openLog(d.code, d.ackExecId, i18n.ackStatus) },
            ackStatus: { value: getStatusLabel(d.ackStatus) },

            nullValue4: { value: getStatusIcon(d.lastExecStatus), onClick: () => openLog(d.code, d.lastExecId, i18n.lastExecStatus) },
            lastExecution: { value: d.lastExecDate, color: d.lastExecColor, format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date', onClick: () => openLog(d.code, d.lastExecId, i18n.lastExecStatus) },
            lastExecStatus: { value: getStatusLabel(d.lastExecStatus) },
            station: { id: d.stationId, code: d.code, name: d.name, city: d.city },
        }))
        const stations = rawData.map(s => s.station)
        dispatch(AdministrationAction.setSelectedSearchValues('piezometry', { stations }))
        return rawData
    }, [exportData])

    useEffect(() => {
        dispatch(PiezometryAction.fetchNetworkPiezoLink()).then(() => {
            const networkGroup = groupBy(networkPiezoLink, 'idNetwork')
            setNetworkList(Object.keys(networkGroup).map(nId => networks.find(n => n.id == nId)).filter(n => !!n))
        })
        if (!networks.length) {
            dispatch(NetworkAction.fetchNetworks())
        }
        onValidate()
    }, [])

    useEffect(() => setActions({
        export: () => {
            const data = values
            if (data.length) {
                data[0].headers = ['code', 'name', 'city', 'startDate', 'endDate', 'numNum', 'exportStatus', 'exportName', 'ackStatus', 'acknoledgment', 'lastExecStatus', 'lastExecution']
            }
            return {
                data,
                exportType: 'xlsx',
                titleFile: i18n.exportOverview,
            }
        },
    }), [values])

    const redirectExecution = (jobId, jobExecutionId) => dispatch(push(`/import/${jobId}?jobExecutionId=${jobExecutionId}`))

    const getLogDialog = () => {
        const data = orderBy(jobLogs, 'date').map(log => ({
            date: { value: getFullDate(log.date), ...getStatusColor(log.status) },
            comment: { value: log.comment, ...getStatusColor(log.status) },
        }))
        const redirect = !jobLogs.length ? () => {} : () => redirectExecution(jobLogs[0].jobId, selectedExecutionId)

        return (
            <Dialog
                onClose={ () => setLogDialogIsOpen(false) }
                fullWidth
                maxWidth='lg'
                open={logDialogIsOpen}
                classes='no-padding'
                scroll='body'
            >
                <DialogTitle>{`${selectedStatusLabel} - ${selectedStationCode}`}</DialogTitle>
                <DialogContent>
                    <NewTable
                        rows={data}
                        headers={['date', 'comment']}
                        rowsPerPageOptions={nbPerPageLabel}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={ () =>setLogDialogIsOpen(false) } variant='outlined'>
                        {i18n.close}
                    </Button>
                    <Button onClick={redirect} variant='contained' color='primary'>
                        {i18n.viewJob}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <div>
            <Card sx={{ padding: 1 }} >
                <Grid container spacing={1}>
                    <Grid size={2}>
                        <Input title={i18n.endDate} value={getDate(date)} onChange={v => onChangeDate(v, setDate)} />
                    </Grid>
                    <Grid size={3}>
                        <Input title={i18n.search} value={searchValue} onChange={setSearchValue} />
                    </Grid>
                    <Grid size={3}>
                        <FilterSelect
                            onChange={(v) => {
                                setFilter(filter)
                                setStationsFilter(v)
                            }}
                            stationType='piezometry'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select options={networkList}
                            keyLabel='mnemonic'
                            label={i18n.network}
                            nullLabel='&nbsp;'
                            onChange={(value = {}) => setNetwork(value)}
                            value={network}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1}>
                    <Grid size={3}>
                        <NumberField title={i18n.nbDaysUndetected} onChange={setNbDaysWarning} value={nbDaysWarning} max={nbDaysError} min={0} />
                    </Grid>
                    <Grid size={3}>
                        <NumberField title={i18n.nbDaysAlert} onChange={setNbDaysError} value={nbDaysError} min={nbDaysWarning} />
                    </Grid>
                    <Grid size={2}>
                        <Select options={statusList} label={i18n.exportStatus} value={exportStatus} onChange={setExportStatus} nullLabel='&nbsp;' />
                    </Grid>
                    <Grid size={2}>
                        <Select options={statusList} label={i18n.ackStatus} value={ackStatus} onChange={setAckStatus} nullLabel='&nbsp;' />
                    </Grid>
                    <Grid size={2}>
                        <Select options={statusList} label={i18n.lastExecStatus} value={lastExecStatus} onChange={setLastExecStatus} nullLabel='&nbsp;' />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid size={1}>
                        <Checkbox
                            checked={color === 'all'}
                            label={i18n.all}
                            onChange={() => setColor('all')}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={color === 'green'}
                            label={
                                <span>
                                    <Box
                                        sx={{
                                            backgroundColor: PASTEL_COLOR.green,
                                            width: '25px',
                                            height: '20px',
                                            borderRadius: '10px',
                                        }}
                                    />
                                    {`(${greenCount || '0'})`}
                                </span>
                            }
                            onChange={v => setColor(v ? 'green' : 'all')}
                            tooltip={template(i18n.infDaysData)({ days: nbDaysWarning })}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={color === 'orange'}
                            label={
                                <span>
                                    <Box
                                        sx={{
                                            backgroundColor: PASTEL_COLOR.yellow,
                                            width: '25px',
                                            height: '20px',
                                            borderRadius: '10px',
                                        }}
                                    />
                                    {`(${orangeCount || '0'})`}
                                </span>
                            }
                            onChange={v => setColor(v ? 'orange' : 'all')}
                            tooltip={template(i18n.infBetweenDaysData)({ days1: nbDaysWarning, days2: nbDaysError })}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Checkbox
                            checked={color === 'red'}
                            label={
                                <span>
                                    <Box
                                        sx={{
                                            backgroundColor: PASTEL_COLOR.red,
                                            width: '25px',
                                            height: '20px',
                                            borderRadius: '10px',
                                        }}
                                    />
                                    {`(${redCount || '0'})`}
                                </span>
                            }
                            onChange={v => setColor(v ? 'red' : 'all')}
                            tooltip={template(i18n.supDaysData)({ days: nbDaysError })}
                        />
                    </Grid>
                    <Grid size='grow' container justifyContent='flex-end'>
                        <Button variant='contained' onClick={() => onValidate()} disabled={!dataLoaded}>
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </Card>
            {!dataLoaded
                ? <ProgressCard progress={progress} />
                : (
                    !exportData.length
                        ? <NoDataCard />
                        : (
                            <CardTable
                                title={i18n.piezometers}
                                color={darkBlue}
                                rows={values}
                                headers={['nullValue', 'code', 'name', 'city', 'startDate', 'endDate', 'numNum', 'nullValue2', 'exportName', 'nullValue3', 'acknoledgment', 'nullValue4', 'lastExecution']}
                                rowsPerPageOptions={nbPerPageLabel}
                            />
                        )
                )}
            {getLogDialog()}
        </div>
    )
}

export default ExporOverviewList